<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from "vue";
import DrawerMenuContent from "./DrawerMenuContent.vue";
import PaperSpaceDialog from "./PaperSpaceDialog.vue";
import DRDialog from "@/components/UI/feedbacks/DRDialog.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRDropdownItem from "@/components/UI/DRDropdownItem.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { Facade } from "@/drawing";
import { useTranslation } from "i18next-vue";
import { trackTab } from "@/analytics/drawing-analytics";

const { t } = useTranslation();

const layouts = ref<string[]>([]);
const activeLayout = ref("Model");
const showCreateDialog = ref(false);

const showRenameDialog = ref(false);
const renamingLayout = ref("");
const renameValue = ref("");

let unsubscribeFileOpen: (() => void) | undefined;

const syncLayouts = () => {
  if (!Facade.isInitialized) return;
  layouts.value = Facade.layouts.getNames();
  activeLayout.value = Facade.layouts.getActive();
};

const switchLayout = (name: string) => {
  if (name === activeLayout.value) return;
  trackTab("paper_space", "layout_changed", { layout: name });
  activeLayout.value = name;
  Facade.layouts.setActive(name);
};

const onLayoutCreated = () => {
  trackTab("paper_space", "layout_created");
  syncLayouts();
};

const startRename = (name: string) => {
  renamingLayout.value = name;
  renameValue.value = name;
  showRenameDialog.value = true;
};

const confirmRename = () => {
  const newName = renameValue.value.trim();
  if (!newName || newName === renamingLayout.value) {
    showRenameDialog.value = false;
    return;
  }
  trackTab("paper_space", "layout_renamed", { layout: newName });
  Facade.layouts.rename(renamingLayout.value, newName);
  showRenameDialog.value = false;
  syncLayouts();
};

const duplicate = (name: string) => {
  trackTab("paper_space", "layout_duplicated", { layout: name });
  Facade.layouts.duplicate(name);
  syncLayouts();
};

const doDelete = (name: string) => {
  const msg = t("canvas:settings.paperSpace.deleteConfirm", { name });
  if (!window.confirm(msg)) return;
  trackTab("paper_space", "layout_deleted", { layout: name });
  Facade.layouts.delete(name);
  syncLayouts();
};

onMounted(() => {
  syncLayouts();
  unsubscribeFileOpen = Facade.onFileOpen(syncLayouts);
});

onBeforeUnmount(() => {
  unsubscribeFileOpen?.();
});
</script>

<template>
  <DrawerMenuContent
    :label="$t('canvas:settings.paperSpace.section.title')"
    :description="$t('canvas:settings.paperSpace.section.description')"
    :actions="true"
  >
    <div v-if="layouts.length === 0" class="text-center text-grey py-4">
      <small>{{ $t("canvas:settings.paperSpace.empty") }}</small>
    </div>

    <div v-else>
      <div
        v-for="name in layouts"
        :key="name"
        class="layout-item d-flex align-items-center canvas-hoverable rounded px-2 gap-2"
        :class="{ active: name === activeLayout }"
      >
        <!-- Icon + name -->
        <button
          class="layout-btn d-flex align-items-center gap-2 col min-w-0 py-2"
          @click="switchLayout(name)"
        >
          <PhosphorIcon
            :name="name === 'Model' ? 'cube' : 'article'"
            :size="18"
            class="flex-shrink-0"
          />
          <span class="text-truncate">{{ name }}</span>
          <PhosphorIcon
            v-if="name === activeLayout"
            name="check"
            :size="14"
            class="flex-shrink-0 ms-auto text-white"
          />
        </button>

        <!-- Three-dot dropdown for paper spaces only -->
        <el-dropdown
          v-if="name !== 'Model'"
          trigger="click"
          @click.stop.prevent
        >
          <DRBtnIcon
            phosphor-icon="dots-three"
            variant="flat-white"
            :size="25"
            @click.stop.prevent
            @dblclick.stop.prevent
          />
          <template #dropdown>
            <el-dropdown-menu>
              <DRDropdownItem
                phosphor-icon="pencil-simple"
                :label="$t('canvas:settings.paperSpace.action.rename')"
                @select="startRename(name)"
              />
              <DRDropdownItem
                phosphor-icon="copy"
                :label="$t('canvas:settings.paperSpace.action.duplicate')"
                @select="duplicate(name)"
              />
              <DRDropdownItem
                phosphor-icon="trash"
                :label="$t('canvas:settings.paperSpace.action.delete')"
                type="danger"
                @select="doDelete(name)"
              />
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>

    <template #actions>
      <button
        class="btn btn-sm btn-outline-light w-100 d-flex align-items-center justify-content-center gap-2"
        :disabled="!Facade.isInitialized || layouts.length === 0"
        @click="showCreateDialog = true"
      >
        <PhosphorIcon name="plus" :size="16" />
        {{ $t("canvas:settings.paperSpace.addLayout") }}
      </button>
    </template>
  </DrawerMenuContent>

  <PaperSpaceDialog v-model="showCreateDialog" @created="onLayoutCreated" />

  <DRDialog
    v-model="showRenameDialog"
    :title="$t('canvas:settings.paperSpace.rename.title')"
    :confirm-label="$t('canvas:settings.paperSpace.rename.confirm')"
    :width="360"
    @confirm="confirmRename"
    @cancel="showRenameDialog = false"
  >
    <input
      v-model="renameValue"
      class="form-control"
      @keyup.enter="confirmRename"
    />
  </DRDialog>
</template>

<style lang="scss" scoped>
.canvas-hoverable {
  background-color: var(--canvas-bg);
  transition: background-color 0.1s ease-in-out;

  &:hover {
    background-color: var(--canvas-border);
  }
}

.layout-item {
  color: rgba(255, 255, 255, 0.6);
  transition: color 0.15s;

  &:hover,
  &.active {
    color: white;
  }

  &.active {
    font-weight: 600;
  }
}

.layout-btn {
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  min-width: 0;
  padding-top: 0;
  padding-bottom: 0;
  text-align: left;
}
</style>
